import qs from 'query-string';
import get from 'lodash/get';
import {useState, useEffect, useRef} from 'react';
import {useRouteMatch, useHistory} from 'react-router-dom';
import useUndoableState from '../../../hooks/useUndoableState';
import usePrevious from '../../../hooks/usePrevious';
import {getPriceFilterByValue} from '../../../constants/priceFilters';
import {getStatusFilterByValue} from '../../../constants/statusFilters';
import {getTypeFilterByValue} from '../../../constants/typeFilters';
import {FEATURED} from '../../../constants/sortOptions';

const useItemFilters = () => {
  const history = useHistory();
  const search = get(history, ['location', 'search'], null);
  const isMatch = useRouteMatch('/manage-registry');
  const isManageRegistry = !!isMatch;
  const firstRender = useRef(true);
  const params = qs.parse(search);
  const [sort, setSort] = useState(params.sort || FEATURED);
  const [page, setPage] = useState(Number(params.page) || 1);

  const [
    filters,
    setFilters,
    {undo: undoFilters, canUndo: canUndoFilters, reset: resetFilters},
  ] = useUndoableState({
    price: (getPriceFilterByValue(params.price) && params.price) || '',
    status: (getStatusFilterByValue(params.status) && params.status) || '',
    type: (getTypeFilterByValue(params.type) && params.type) || '',
    stores: params.stores || '',
  });

  const hasAppliedFilters =
    !!filters.price || !!filters.status || !!filters.type || !!filters.stores;

  const previousFilters = usePrevious(filters);
  const previousPage = usePrevious(page);
  const previousSort = usePrevious(sort);

  useEffect(() => {
    const {
      price = '',
      status = '',
      type = '',
      stores = '',
      page: newPage,
      sort: newSort,
    } = params;

    if (isManageRegistry) {
      setPage(parseInt(newPage, 10) || page);
      setSort(newSort || sort);
      setFilters({
        price,
        status,
        type,
        stores,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /**
     * Don't set the URL parameters from item filters on the first
     * render. Instead, set the initial filter state from
     * the URL parameters (if any).
     */
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (isManageRegistry) {
      history.replace({
        search: qs.stringify({
          page,
          sort,
          price: filters.price || undefined,
          status: filters.status || undefined,
          type: filters.type || undefined,
          stores: filters.stores || undefined,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    sort,
    filters.price,
    filters.status,
    filters.type,
    filters.stores,
    isManageRegistry,
  ]);

  return {
    filters,
    previousFilters,
    previousPage,
    previousSort,
    setFilters,
    undoFilters,
    canUndoFilters,
    resetFilters,
    sort,
    page,
    setSort,
    setPage,
    hasAppliedFilters,
  };
};

export default useItemFilters;
